import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import Icon from '../components/Icon';
import Logo from '../components/Logo';
import { useProducts } from '../product/ProductContext';
import '../shell/shell.css';
import './onboarding.css';
import SourcesStep from './SourcesStep';
import ReviewStep from './ReviewStep';
import QuizStep from './QuizStep';
import IcpStep from './IcpStep';
import FirstPostsStep from './FirstPostsStep';
import {
    STEPS, answersForServer, applyAnswers, channelsFromIcps, emptyFields, fieldSourcesOf, fieldsToProduct, isAnswered,
    questionsForGaps, serializeDraft, sourcesToProduct,
} from './onboardingUtils';
import { isLeaving } from '../auth/leaving';

const INITIAL_DRAFT = {
    step: 'sources',
    sources: [],
    fields: null,
    sourceCounts: {},
    questions: [], // suggested by the extractor
    quiz: [], // the questions actually asked, fixed when the quiz starts
    answers: {},
    qIndex: 0,
    icps: null,
    icpsFrom: null, // the answers the ICP drafts were made from
};

// Every field present, in the { value, source, confidence } shape
const normalizeFields = (fields = {}) => Object.entries(emptyFields()).reduce((acc, [id, empty]) => ({
    ...acc,
    [id]: { ...empty, ...(fields[id] || {}) },
}), {});

const Stepper = ({ step }) => {
    const current = STEPS.findIndex(s => s.id === step);
    return (
        <div className="ls-stepper" aria-label={`Step ${current + 1} of ${STEPS.length}`}>
            {STEPS.map((s, index) => {
                const state = index < current ? 'is-done' : index === current ? 'is-current' : '';
                return (
                    <React.Fragment key={s.id}>
                        {index > 0 && <span className="ls-stepper-line" />}
                        <span className={`ls-stepper-step ${state}`} aria-current={index === current ? 'step' : undefined}>
                            <span className="ls-stepper-dot">
                                {index < current ? <Icon name="check" size={14} /> : index + 1}
                            </span>
                            <span className="ls-stepper-label">{s.label}</span>
                        </span>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const AddProductPage = ({ user, authLoading = false }) => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { products, isLoading, createProduct, updateProduct, selectProduct, setIcps } = useProducts();
    // /products/:id/setup resumes; /products/new?id= is a new product that has been saved once
    const routeId = params.productId || searchParams.get('id');
    const [productId, setProductId] = useState(routeId || null);
    const [draft, setDraft] = useState(INITIAL_DRAFT);
    const [isRestored, setIsRestored] = useState(!routeId);
    const [isBusy, setIsBusy] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!authLoading && !user && !isLeaving()) navigate('/?showAuth=true');
    }, [navigate, user, authLoading]);

    // Resume from the draft saved on the product
    useEffect(() => {
        if (isRestored || isLoading || !routeId) return;
        const restore = (product) => {
            if (!product || product.setupStep === 'done') {
                navigate('/dashboard', { replace: true });
                return;
            }
            const saved = product.onboarding || {};
            setDraft({
                ...INITIAL_DRAFT,
                ...saved,
                fields: saved.fields ? normalizeFields(saved.fields) : null,
                step: !STEPS.some(s => s.id === product.setupStep) ? 'sources'
                    : product.setupStep === 'quiz' && !saved.quiz?.length ? 'review'
                        : product.setupStep,
            });
            setProductId(product.id);
            setIsRestored(true);
        };
        const known = products.find(p => p.id === routeId);
        if (known) restore(known);
        else api.getProduct(routeId).then(restore).catch(() => navigate('/dashboard', { replace: true }));
    }, [isRestored, isLoading, routeId, products, navigate]);

    // Saves the draft (and whatever the profile knows so far) on the product, creating it the first time
    const persist = useCallback(async (next, extra = {}) => {
        const hasQuiz = next.quiz.some(q => isAnswered(next.answers[q.field]));
        const name = next.fields?.name?.value || next.sources.find(s => s.status === 'ready')?.title || 'New product';
        const payload = {
            ...(next.fields && { ...fieldsToProduct(next.fields), fieldSources: fieldSourcesOf(next.fields) }),
            name: String(name).slice(0, 120),
            sources: sourcesToProduct(next.sources, hasQuiz),
            setupStep: next.step,
            onboarding: serializeDraft(next),
            ...extra,
        };
        if (productId) return updateProduct(productId, payload);
        const created = await createProduct(payload);
        setProductId(created.id);
        // Same route, so this page keeps its state; a refresh resumes the saved draft
        navigate(`/products/new?id=${created.id}`, { replace: true });
        return created;
    }, [productId, updateProduct, createProduct, navigate]);

    // Moves to the next draft state once it's saved, so later steps can rely on the product
    const goTo = async (next, extra) => {
        setIsBusy(true);
        setError('');
        try {
            await persist(next, extra);
            setDraft(next);
            window.scrollTo(0, 0);
            return true;
        } catch (e) {
            // A plan limit (e.g. one product on Free) also opens the upgrade prompt
            setError(e.response?.status === 402 ? e.response.data.error : "Couldn't save your progress. Check your connection and try again.");
            return false;
        } finally {
            setIsBusy(false);
        }
    };

    const handleSaveAndExit = async () => {
        if (draft.step === 'first') {
            navigate('/dashboard');
            return;
        }
        const hasContent = draft.sources.length > 0 || draft.fields;
        if (!productId && !hasContent) {
            navigate('/dashboard');
            return;
        }
        if (await goTo(draft)) navigate('/dashboard');
    };

    const handleAnalyze = async () => {
        setIsAnalyzing(true);
        setError('');
        try {
            // Page text isn't kept in the saved draft, so resumed URL sources are read again
            const sources = await Promise.all(draft.sources
                .filter(source => source.status === 'ready')
                .map(async source => {
                    if (source.text || source.file?.dataUrl || !source.url) return source;
                    const read = await api.readSource(source.type, source.url);
                    return { ...source, text: read.text };
                }));
            const payload = sources
                .filter(source => source.text || source.file?.dataUrl)
                .map(source => ({
                    id: source.id,
                    type: source.type,
                    title: source.title,
                    ...(source.text && { text: source.text }),
                    ...(source.file?.dataUrl && { file: { name: source.file.name, dataUrl: source.file.dataUrl } }),
                }));
            if (payload.length === 0) {
                throw Object.assign(new Error('Nothing to read'), { userMessage: 'Add a source (or upload the file again) before analyzing.' });
            }
            const result = await api.extractProduct(payload);
            const next = {
                ...draft,
                sources: draft.sources.map(source => sources.find(s => s.id === source.id) || source),
                step: 'review',
                fields: normalizeFields(result.fields),
                sourceCounts: result.sourceCounts || {},
                questions: result.questions || [],
                quiz: [],
                answers: {},
                qIndex: 0,
                icps: null,
            };
            await goTo(next, { syncedAt: new Date().toISOString() });
        } catch (e) {
            setError(e.userMessage || e.response?.data?.error || "Couldn't analyze your sources. Please try again.");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleSkip = () => goTo({
        ...draft,
        step: 'review',
        fields: draft.fields || normalizeFields(),
    });

    const startQuiz = () => {
        const quiz = questionsForGaps(draft.fields, draft.questions);
        if (quiz.length === 0) {
            goTo({ ...draft, step: 'icps', quiz: [] });
            return;
        }
        goTo({ ...draft, step: 'quiz', quiz, qIndex: 0 });
    };

    const finishQuiz = (answers) => goTo({
        ...draft,
        answers,
        fields: applyAnswers(draft.fields, draft.quiz, answers),
        step: 'icps',
        // New answers change who the ICPs are for, so draft them again
        icps: draft.icpsFrom === JSON.stringify(answersForServer(answers)) ? draft.icps : null,
    });

    const handleFinish = async (keptIcps) => {
        setIsBusy(true);
        setError('');
        try {
            const saved = await api.replaceIcps(productId, keptIcps);
            const current = products.find(p => p.id === productId);
            await updateProduct(productId, {
                setupStep: 'done',
                onboarding: null,
                setupDone: { profile: true, icps: true },
                ...(!current?.channels?.length && { channels: channelsFromIcps(saved) }),
            });
            selectProduct(productId);
            setIcps(saved, productId);
            // Setup is done, but the first thing they see is posts for their customers, not an empty dashboard
            setDraft(prev => ({ ...prev, step: 'first', icps: saved }));
            window.scrollTo(0, 0);
        } catch (e) {
            setError("Couldn't save your ICPs. Please try again.");
        } finally {
            setIsBusy(false);
        }
    };

    // Same top bar as the flow itself, so only the step's content arrives later
    if (!isRestored || authLoading) {
        return (
            <div className="ls-flow">
                <header className="ls-flow-bar">
                    <div className="ls-crumb">
                        <Logo size={26} />
                        <span style={{ color: 'var(--border-input)' }}>/</span>
                        <span className="ls-crumb-product" style={{ fontSize: 14, fontWeight: 600 }}>New product</span>
                    </div>
                </header>
                <div style={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                    <span className="ls-spinner" style={{ width: 28, height: 28 }} />
                </div>
            </div>
        );
    }

    const productName = draft.fields?.name?.value || 'your product';

    return (
        <div className="ls-flow">
            <header className="ls-flow-bar">
                <div className="ls-crumb">
                    <Logo size={26} />
                    <span style={{ color: 'var(--border-input)' }}>/</span>
                    <span className="ls-crumb-product" style={{ fontSize: 14, fontWeight: 600 }}>New product</span>
                </div>
                <Stepper step={draft.step} />
                <button type="button" className="ls-btn ls-btn-ghost" onClick={handleSaveAndExit} disabled={isBusy || isAnalyzing}>
                    Save &amp; exit
                </button>
            </header>

            {error && (
                <div className="ls-error" role="alert" style={{ margin: '16px 28px 0' }}>
                    <Icon name="error" /><span style={{ flex: 1 }}>{error}</span>
                    <button type="button" className="ls-chip-remove" aria-label="Dismiss" onClick={() => setError('')}><Icon name="close" size={18} /></button>
                </div>
            )}

            {draft.step === 'sources' && (
                <SourcesStep
                    sources={draft.sources}
                    setDraft={setDraft}
                    isAnalyzing={isAnalyzing}
                    isBusy={isBusy}
                    onAnalyze={handleAnalyze}
                    onSkip={handleSkip}
                />
            )}
            {draft.step === 'review' && (
                <ReviewStep
                    draft={draft}
                    setDraft={setDraft}
                    isBusy={isBusy}
                    onContinue={startQuiz}
                    onAddSource={() => setDraft(prev => ({ ...prev, step: 'sources' }))}
                />
            )}
            {draft.step === 'quiz' && draft.quiz.length > 0 && (
                <QuizStep
                    draft={draft}
                    productName={productName}
                    isBusy={isBusy}
                    onBack={() => setDraft(prev => ({ ...prev, step: 'review' }))}
                    onDone={finishQuiz}
                    onProgress={(answers, qIndex) => setDraft(prev => ({ ...prev, answers, qIndex }))}
                />
            )}
            {draft.step === 'first' && productId && (
                <FirstPostsStep
                    productId={productId}
                    productName={products.find(p => p.id === productId)?.name || draft.fields?.name?.value || 'Your product'}
                    icps={draft.icps || []}
                />
            )}
            {draft.step === 'icps' && productId && (
                <IcpStep
                    productId={productId}
                    draft={draft}
                    setDraft={setDraft}
                    isBusy={isBusy}
                    onBack={() => setDraft(prev => ({ ...prev, step: prev.quiz.length ? 'quiz' : 'review' }))}
                    onFinish={handleFinish}
                />
            )}
        </div>
    );
};

export default AddProductPage;
